import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { Button, ControlBar, ICON, Skeleton } from '@attendance/ui';
import { SearchBox } from '../../components/common/SearchBox.js';
import {
  type CompanyInfo,
  type CompanySettings,
} from '@attendance/shared/schemas/settings';
import { api } from '../../lib/api.js';
import { settingsApi } from '../../api/settings.api.js';
import { employeesApi } from '../../api/employees.api.js';
import { useBrandingStore, useFeatures } from '../../stores/brandingStore.js';
import { PageShell } from '../../components/common/PageShell.js';
import { friendlyMessage } from '../../lib/errors.js';
import { useConfirm } from '../../lib/useConfirm.js';
import { useAdminLockActive } from '../../components/lock/useAdminLock.js';
import { AppLockSettingsSection } from './AppLockSettingsSection.js';
import { isAdminLockedGroup } from './adminLockedGroups.js';
import { FieldRow } from './SettingsFieldRow.js';
import { SettingsGroupNav, SETTINGS_GROUP_SCROLL_MARGIN } from './SettingsGroupNav.js';
import { useOptionalCopy } from './useOptionalCopy.js';
import { SettingsSaveBar, SettingsSaveFailure } from './SettingsSaveBar.js';
import { draftEdits, useCompanyDraft, type CompanyEdits } from './useCompanyDraft.js';
/* The four blocks this page draws around its sections — see `SettingsBlocks`. */
import {
  CompanyIdentityBlock,
  LetterTokens,
  ManagedElsewhere,
} from './SettingsBlocks.js';
import { FeaturePresets } from './FeaturePresets.js';
import {
  ALL_FIELDS,
  FIELD_BY_ID,
  SETTING_GROUPS,
  type EditableCategory,
  type SettingField,
} from './settingsCatalog.js';
import { buildPatch, readPath, sameValue, writePath } from './settingsPatch.js';
import {
  PayTag,
  SectionShell,
  WorkdayEngineOffAlert,
  formatSettingValue,
} from './SettingsControls.js';

/**
 * Settings.
 *
 * Written for an admin who opens this screen rarely, to change one thing,
 * usually while somebody is waiting. Four things follow from that:
 *
 *  1. **A setting that changes pay says so.** Overtime rates, the workday
 *     target, grace, break windows, Ramadan, and every rule that can REJECT a
 *     punch (a rejected punch is unpaid time) carry an "Affects pay" marker,
 *     show a before → after line the instant they are edited, and are read back
 *     one by one in a confirm step before anything is written.
 *  2. **Grouping follows what a setting controls, not which table it lives in.**
 *     `weekendDays` is stored under `general` but decides who gets the weekend
 *     premium, so it sits with the overtime rules. Ramadan is stored under
 *     `uaeCompliance` but shortens the paid day, so it sits with the workday.
 *  3. **Nothing is hidden behind a disclosure.** Every value is on screen, so
 *     the page can be audited by scrolling it. Search is how you navigate ~100
 *     settings, not a tree you have to open six times.
 *  4. **The save is a partial patch.** See `settingsPatch.ts` — this page never
 *     sends a whole category object, because doing so re-asserts every field it
 *     happens to be holding and stamps out a concurrent edit.
 */

type FieldValues = Record<string, unknown>;

/** Stable empty set, so "not searching" never invalidates the group memo. */
const NO_IDS: ReadonlySet<string> = new Set<string>();

interface FailedSave {
  /** The edits that were reverted, so Retry can put them back. */
  values: FieldValues;
  /** The same, for the company record — it rides in the same batch. */
  company?: CompanyEdits;
  message: string;
}

/**
 * `fieldReviewMode: "verify"` is selected and nothing can satisfy it.
 *
 * The sibling of `WorkdayEngineOffAlert`, deliberately: same `alert-warning`
 * shell, same place above the fields rather than beside them, same refusal to
 * offer a one-click fix. Moving off `verify` changes how punches are treated,
 * and this screen's whole posture is that a pay-affecting change is confirmed,
 * not nudged.
 *
 * The body is the SERVER's sentence, rendered verbatim. What makes `verify`
 * inert is a property of which clients have shipped — the server tracks it
 * (`SettingsService.getFaceReviewModeStatus`), so the server says it. A
 * paraphrase here would be a second statement of one fact, free to drift, and
 * would still read "no client uploads a descriptor" on the day one does. The
 * heading is the only client-authored line and it says nothing the status does
 * not: it exists so the alert has a scannable first line.
 *
 * Not `role="alert"`: this is present on load, not announced by an action, and
 * an assertive live region firing on page render interrupts the screen-reader
 * user mid-sentence. `role="status"`, matching WorkdayEngineOffAlert.
 */
function FaceReviewInoperativeAlert({ reason }: { reason: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div role="status" className="alert-warning">
      <AlertTriangle size={ICON.feature} aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="min-w-0 space-y-element">
        <p className="font-semibold">
          {t(
            'settings.faceReviewMode.inoperative.title',
            'Server-side face re-verification is not running'
          )}
        </p>
        <p>{reason}</p>
      </div>
    </div>
  );
}

export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const optional = useOptionalCopy();
  const qc = useQueryClient();
  const features = useFeatures();
  const { confirm, ConfirmPortal } = useConfirm();

  // ONE request for every category. `/settings/all` is what the WPS export page
  // already uses; fetching per-category would mean 13 round-trips and would make
  // "search across all settings" a lie until every tab had been visited.
  const settings = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => settingsApi.getAll(),
  });
  const data = settings.data;

  // Whether the flexible net-hours + overtime engine is actually running is
  // NOT in the payload above and cannot be derived from it: the schema's
  // `.default(9)` / `.default(30)` make "never configured" and "configured to
  // exactly the defaults" identical over the wire, while the rollup branches on
  // the raw stored JSON and computes zero overtime for the first. A second,
  // side-effect-free request is the only honest source. It shares the
  // `['settings']` prefix, so the post-save invalidation below re-reads it —
  // saving the pay rules is what activates the engine, and the warning has to
  // clear on its own when it does.
  const workdayEngine = useQuery({
    queryKey: ['settings', 'workday-status'],
    queryFn: () => settingsApi.workdayStatus(),
  });
  // Only an explicit `false` warns. An unresolved or failed status is "we do
  // not know", and telling an admin their overtime is off on a guess would be
  // its own lie.
  const engineOff = workdayEngine.data?.active === false;
  const engineReason =
    workdayEngine.data?.reason === 'malformed' ? 'malformed' : 'not-configured';

  // The same question, for the same reason, about a different setting.
  // `fieldReviewMode: 'verify'` promises a server-side re-run of the face match
  // before a punch counts — and no field client has ever uploaded the
  // descriptor that would need, so the mode is inert. `/settings/all` reports
  // it as configuration, indistinguishably from a mode that works, which is the
  // shape this screen already refuses to show for the workday engine. Same
  // `['settings']` prefix, so a save that switches the mode re-reads it.
  const faceReview = useQuery({
    queryKey: ['settings', 'face-review-status'],
    queryFn: () => settingsApi.faceReviewStatus(),
  });
  // Only an explicit `true` warns. Unresolved or failed is "we do not know",
  // and guessing that a pay-affecting setting is dead would be its own lie.
  const faceReviewInoperative = faceReview.data?.inoperative === true;
  // Server-authored and rendered verbatim — the server is the half that knows
  // whether any shipped client can satisfy the mode. Paraphrasing it here would
  // be a second, drifting statement of one fact.
  const faceReviewReason = faceReview.data?.reason ?? '';

  /* THE COMPANY RECORD IS PART OF THIS PAGE'S DRAFT, not a form of its own —
     see `useCompanyDraft`. Read on every tab, because an edit made on the
     Company tab has to stay counted in the bar from every other one. */
  const company = useCompanyDraft();
  const [edits, setEdits] = useState<FieldValues>({});
  const [query, setQuery] = useState('');
  const [failed, setFailed] = useState<FailedSave | null>(null);

  const serverValue = useCallback(
    (field: SettingField): unknown =>
      data ? readPath(data[field.category], field.path) : undefined,
    [data]
  );

  const currentValue = useCallback(
    (field: SettingField): unknown =>
      field.id in edits ? edits[field.id] : serverValue(field),
    [edits, serverValue]
  );

  /** Fields whose current value genuinely differs from what the server holds. */
  const dirtyFields = useMemo(() => {
    if (!data) return [] as SettingField[];
    return ALL_FIELDS.filter(
      (f) => f.id in edits && !sameValue(edits[f.id], readPath(data[f.category], f.path))
    );
  }, [data, edits]);

  const dirtyPayFields = useMemo(() => dirtyFields.filter((f) => f.pay), [dirtyFields]);
  const dirtyIds = useMemo(
    () => new Set(dirtyFields.map((f) => f.id)),
    [dirtyFields]
  );
  const pendingCount = dirtyFields.length + company.dirtyCount;
  /* WHY THE SAVE CANNOT GO OUT, in words the bar prints (§3.11). */
  const companyObstacle = company.obstacle
    ? t('settings.blockedByCompany', 'Company info: {{reason}}', {
        reason:
          company.obstacle === 'name'
            ? t('settings.companyInfo.nameRequired', 'Company name is required.')
            : t('settings.company.currencyInvalid', 'Use a three-letter currency code, like AED.'),
      })
    : null;
  const isDirty = pendingCount > 0;

  // Warn before a reload / close while edits are unsaved. In-app navigation is
  // not covered by `beforeunload`; the sticky bar keeps the pending count in
  // view instead of relying on the browser prompt.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const setField = useCallback((id: string, value: unknown): void => {
    setEdits((prev) => ({ ...prev, [id]: value }));
    setFailed(null);
  }, []);

  // Save. One PATCH per affected category (plus the company record), each body
  // carrying ONLY the paths that changed. A request that fails has its edits
  // rolled back so the screen keeps showing what the server actually holds — a
  // failed save must never look like a saved one — and the reverted values are
  // stashed so Retry can put them straight back.
  const save = useMutation({
    mutationFn: async ({
      fields,
      companyPatch,
    }: {
      fields: SettingField[];
      /** `PATCH /settings/info`'s body, or null when the record is clean. */
      companyPatch: Partial<CompanyInfo> | null;
    }) => {
      const byCategory = new Map<EditableCategory, SettingField[]>();
      for (const field of fields) {
        const bucket = byCategory.get(field.category);
        if (bucket) bucket.push(field);
        else byCategory.set(field.category, [field]);
      }

      const requests = [...byCategory].map(async ([category, categoryFields]) => {
        let draft: unknown = data?.[category];
        const paths: string[] = [];
        for (const field of categoryFields) {
          const value = edits[field.id];
          draft = writePath(draft, field.path, value);
          paths.push(field.path);
          for (const also of field.alsoWrites ?? []) {
            draft = writePath(draft, also, value);
            paths.push(also);
          }
        }
        const patch = buildPatch(draft, paths);
        const response = await api.patch<{ data: unknown }>(
          `/settings/${category}`,
          patch
        );
        return { category, saved: response.data, fields: categoryFields };
      });

      /* THE COMPANY RECORD RIDES IN THE SAME BATCH. `/settings/info` is a
         different resource and so a different request — but the bar was already
         sending one per category, so a second resource is one more request, not
         a second Save button. It takes a PARTIAL body for the same reason a
         category patch does: the route deep-merges over the stored record
         before validating. Settled separately so one resource's failure is
         reported as that resource's. */
      const companyRequest = companyPatch
        ? api.patch<{ data: unknown }>('/settings/info', companyPatch)
        : null;

      const [settled, companySettled] = await Promise.all([
        Promise.allSettled(requests),
        Promise.allSettled(companyRequest ? [companyRequest] : []),
      ]);

      const ok = settled.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
      const errors = [...settled, ...companySettled].flatMap((r) =>
        r.status === 'rejected' ? [r.reason] : []
      );
      return {
        ok,
        errors,
        attempted: fields,
        companyAttempted: companyRequest !== null,
        companySaved: companySettled.every((r) => r.status === 'fulfilled'),
      };
    },
    onSuccess: ({ ok, errors, attempted, companyAttempted, companySaved }) => {
      // Fold whatever landed into the cache so the page shows server truth.
      if (ok.length > 0 && data) {
        const next = { ...data } as CompanySettings;
        for (const result of ok) {
          (next as Record<string, unknown>)[result.category] = result.saved;
        }
        qc.setQueryData(['settings', 'all'], next);
      }
      const savedIds = new Set(ok.flatMap((r) => r.fields.map((f) => f.id)));
      const failedFields = attempted.filter((f) => !savedIds.has(f.id));

      // Capture what is about to be rolled back BEFORE clearing it, so Retry
      // has something to put back.
      const stash: FieldValues = {};
      for (const field of failedFields) stash[field.id] = edits[field.id];
      const companyFailed = companyAttempted && !companySaved;
      const companyStash = companyFailed ? draftEdits(company) : undefined;
      // Same rule as the fields above: what did not save is reverted, so the
      // screen never shows a value nobody is being paid by, and what was
      // reverted is stashed so Retry can put it back.
      if (companyAttempted) company.clear();

      setEdits((prev) => {
        const next = { ...prev };
        // Both saved AND failed edits are dropped: saved ones because the
        // server value now matches, failed ones because leaving them on screen
        // would show a number nobody is being paid by.
        for (const field of attempted) delete next[field.id];
        return next;
      });

      if (failedFields.length > 0 || companyFailed) {
        const message = friendlyMessage(errors[0]);
        setFailed({ values: stash, company: companyStash, message });
        toast.error(message);
      } else {
        setFailed(null);
        toast.success(t('settings.toast.saved'));
      }

      void qc.invalidateQueries({ queryKey: ['settings'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
      void qc.invalidateQueries({ queryKey: ['attendance', 'punch-context'] });
      const touched = new Set(attempted.map((f) => f.category));
      if (touched.has('terminology') || touched.has('features')) {
        void useBrandingStore.getState().refresh();
      }
    },
    onError: (err) => {
      // The whole batch failed before any request resolved (offline, etc.).
      const message = friendlyMessage(err);
      setFailed({ values: { ...edits }, company: draftEdits(company), message });
      setEdits({});
      company.clear();
      toast.error(message);
    },
  });

  /**
   * Turning the site-workers pillar off soft-hides every worker record and
   * stops field/kiosk capture. Quantify the blast radius before it happens; the
   * hide is reversible and never deletes.
   */
  const confirmWorkersOff = useCallback(async (): Promise<boolean> => {
    let count = -1;
    try {
      count = await employeesApi.siteWorkerCount();
    } catch {
      // Warn generically rather than flipping the pillar silently.
      count = -1;
    }
    if (count === 0) return true;
    return confirm({
      title: t('settings.workersOff.title'),
      body:
        count > 0
          ? t('settings.workersOff.bodyCount', { count })
          : t('settings.workersOff.bodyUnknown'),
      confirmLabel: t('settings.workersOff.confirm'),
      cancelLabel: t('settings.workersOff.cancel'),
      destructive: true,
    });
  }, [confirm, t]);

  const handleSave = useCallback(async (): Promise<void> => {
    if (dirtyFields.length === 0 && company.dirtyCount === 0) return;
    // A field the reader has actually edited and cannot legally save (see
    // `useCompanyDraft`). The bar names it rather than saving around it.
    if (company.obstacle) return;

    const workersField = dirtyFields.find((f) => f.id === 'feature.workers');
    if (workersField && edits['feature.workers'] === false) {
      if (!(await confirmWorkersOff())) return;
    }

    if (dirtyPayFields.length > 0) {
      const proceed = await confirm({
        title: t('settings.payConfirm.title'),
        body: (
          <div className="space-y-inline">
            <p className="text-copy">{t('settings.payConfirm.lead')}</p>
            <ul className="space-y-element">
              {/* Nested inside the confirm dialog's body, so `rounded-card`
                  (12px, the inner-card role) is correct and stays. */}
              {dirtyPayFields.map((field) => (
                <li
                  key={field.id}
                  className="rounded-card border border-border bg-muted p-2 text-copy"
                >
                  <span className="block font-medium">
                    {t(`settings.f.${field.id}.label`)}
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-control text-copy-sm tabular-nums">
                    <span className="line-through">
                      {formatSettingValue(field, serverValue(field), t)}
                    </span>
                    <span aria-hidden="true">→</span>
                    <span className="font-semibold">
                      {formatSettingValue(field, edits[field.id], t)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-copy-sm text-muted-foreground">
              {t('settings.payConfirm.scope')}
            </p>
          </div>
        ),
        confirmLabel: t('settings.payConfirm.confirm'),
        cancelLabel: t('settings.payConfirm.cancel'),
        destructive: true,
      });
      if (!proceed) return;
    }

    save.mutate({ fields: dirtyFields, companyPatch: company.patch });
  }, [
    company.dirtyCount,
    company.obstacle,
    company.patch,
    confirm,
    confirmWorkersOff,
    dirtyFields,
    dirtyPayFields,
    edits,
    save,
    serverValue,
    t,
  ]);

  // -------------------------------------------------------------------------
  // Search + feature gating
  // -------------------------------------------------------------------------
  const needle = query.trim().toLowerCase();

  // The dirty set only matters while a search is narrowing the page. Swapping
  // in a stable empty set otherwise keeps an ordinary edit from invalidating
  // the group memo and re-deriving all ~130 rows.
  const protectedIds = needle === '' ? NO_IDS : dirtyIds;

  /**
   * Everything the search box reads, lowercased, built ONCE per language.
   *
   * It used to be assembled per keystroke: for each of ~144 fields, one `t()`
   * plus two `optional()` — and `optional` calls `i18n.exists()`, which walks
   * the resource tree. That is roughly seven hundred i18n lookups and 144
   * string builds for every character typed, on top of the group and section
   * text doing the same again.
   *
   * The copy cannot change between keystrokes; only the needle can. So the
   * lookups happen once, keyed on the translator, and typing becomes a
   * substring test against a string that already exists.
   */
  const searchIndex = useMemo(() => {
    const fields = new Map<string, string>();
    for (const [id, field] of FIELD_BY_ID) {
      fields.set(
        id,
        [
          t(`settings.f.${id}.label`),
          optional(`settings.f.${id}.hint`),
          optional(`settings.f.${id}.impact`),
          id,
          field.path,
        ]
          .join(' ')
          .toLowerCase()
      );
    }
    const groups = new Map<string, string>();
    const sections = new Map<string, string>();
    for (const group of SETTING_GROUPS) {
      groups.set(
        group.id,
        [t(`settings.g.${group.id}.title`), optional(`settings.g.${group.id}.blurb`)]
          .join(' ')
          .toLowerCase()
      );
      for (const section of group.sections) {
        sections.set(
          section.id,
          [
            t(`settings.s.${section.id}.title`),
            optional(`settings.s.${section.id}.blurb`),
          ]
            .join(' ')
            .toLowerCase()
        );
      }
    }
    return { fields, groups, sections };
  }, [optional, t]);

  const fieldMatches = useCallback(
    (field: SettingField): boolean => {
      if (needle === '') return true;
      // An unsaved change is never filtered out of view. Hiding one behind a
      // search box is how an admin saves something they can no longer see.
      if (protectedIds.has(field.id)) return true;
      return (searchIndex.fields.get(field.id) ?? '').includes(needle);
    },
    [needle, protectedIds, searchIndex]
  );

  const visibleGroups = useMemo(() => {
    return SETTING_GROUPS.filter((g) => !g.feature || features[g.feature] !== false)
      .map((group) => {
        const groupHit =
          needle !== '' && (searchIndex.groups.get(group.id) ?? '').includes(needle);
        const sections = group.sections
          // A section can carry the same feature gate a group can — see
          // SettingSection.feature. Applied BEFORE the search filter, so a
          // switched-off module can never be searched back into view.
          .filter((sec) => !sec.feature || features[sec.feature] !== false)
          .map((section) => {
            const sectionHit =
              needle !== '' &&
              (searchIndex.sections.get(section.id) ?? '').includes(needle);
            const fields = group.sections
              ? section.fields.filter(
                  (f) => groupHit || sectionHit || fieldMatches(f)
                )
              : [];
            return { section, fields };
          })
          .filter((s) => s.fields.length > 0);
        // A bespoke editor (schedules, presets, links) shows when nothing is
        // being searched for, or when the group itself matched the search.
        const showCustom = needle === '' || groupHit;
        return { group, sections, showCustom };
      })
      .filter((g) => g.sections.length > 0 || (g.group.custom && g.showCustom));
  }, [features, fieldMatches, needle, searchIndex]);

  /* THE SEARCH BOX IS THE OTHER DOOR INTO A LOCKED GROUP, and it was standing
     open. Search deliberately crosses every group and renders each match's real
     controls, so with the admin PIN now gating three groups by ROUTE, a search
     for "kiosk" run from the pay tab would have drawn the Kiosk lock card — the
     admin PIN's own control — on a page the PIN never asked about. A gate one
     component can walk around is not a gate.

     So while the lock is up those groups are not searchable, and the page SAYS
     they are not: `heldBack` feeds a line above the results, because a search
     that quietly returns 40 of 47 settings is the §3.3 defect this app keeps
     paying for — an admin concludes the setting does not exist and goes looking
     for it somewhere else. */
  const adminLocked = useAdminLockActive();
  const heldBack = useMemo(
    () => (adminLocked ? visibleGroups.filter((g) => isAdminLockedGroup(g.group.id)) : []),
    [adminLocked, visibleGroups]
  );
  const searchableGroups = useMemo(
    () =>
      adminLocked
        ? visibleGroups.filter((g) => !isAdminLockedGroup(g.group.id))
        : visibleGroups,
    [adminLocked, visibleGroups]
  );

  /* Counted over what search can actually REACH, not over the catalogue. A
     count that includes rows the page will not draw promises matches that are
     not there. */
  const matchCount = useMemo(
    () =>
      searchableGroups.reduce(
        (n, g) => n + g.sections.reduce((m, s) => m + s.fields.length, 0),
        0
      ),
    [searchableGroups]
  );

  const dirtyByGroup = useMemo(() => {
    const counts = new Map<string, number>();
    for (const group of SETTING_GROUPS) {
      const ids = new Set(group.sections.flatMap((s) => s.fields.map((f) => f.id)));
      const n = dirtyFields.filter((f) => ids.has(f.id)).length;
      const own = group.custom === 'companyIdentity' ? company.dirtyCount : 0;
      if (n + own > 0) counts.set(group.id, n + own);
    }
    return counts;
  }, [company.dirtyCount, dirtyFields]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const searchInputRef = useRef<HTMLInputElement>(null);

  /*
   * ONE GROUP AT A TIME, AND EACH ONE A URL.
   *
   * This page used to mount all of it: 120 controls across 12 groups, every one
   * in the DOM whether or not anybody was looking at it, reachable only by a
   * `#hash` rail that was `hidden lg:block` — so on a tablet an admin got a
   * 120-field scroll with no navigation at all.
   *
   * The group now comes from the route (`/settings/pay`), which buys three
   * things at once: only the group being read is rendered, the tab strip works
   * at every width, and "the pay rules" is a link you can send to somebody
   * instead of an instruction to scroll.
   *
   * SEARCH STILL SPANS EVERYTHING. That is the reason the old page mounted the
   * lot, and it does not need to: the index is built from the CATALOGUE and the
   * translations, never from what happens to be mounted. While a search is
   * running the tabs step aside and every matching group renders, so a result
   * is never hidden behind a tab the admin has not thought to open.
   */
  const { group: groupParam } = useParams<{ group?: string }>();
  const navigate = useNavigate();
  const searching = needle !== '';

  const activeGroupId =
    visibleGroups.find((g) => g.group.id === groupParam)?.group.id ??
    visibleGroups[0]?.group.id ??
    null;

  // An unknown or now-hidden group in the URL (a stale bookmark, a module
  // switched off since) resolves to the first tab rather than an empty page.
  useEffect(() => {
    if (searching || !activeGroupId) return;
    if (groupParam !== activeGroupId) {
      navigate(`/settings/g/${activeGroupId}`, { replace: true });
    }
  }, [searching, activeGroupId, groupParam, navigate]);

  const shownGroups = searching
    ? searchableGroups
    : visibleGroups.filter((g) => g.group.id === activeGroupId);

  return (
    /* THE FLOOR IS ONLY RESERVED WHEN SOMETHING IS STANDING ON IT.

       This was an unconditional `pb-32` — 128px of empty page under every
       settings tab, on every visit, held open for a save bar that renders only
       while there are unsaved edits. That is the state an admin is in for the
       whole of a read-only visit, which is most of them, and it cost the
       bottom-most card of every group its place above the fold for nothing.

       Reserved while `isDirty`, released otherwise. It is still a fixed figure
       for a bar whose height depends on whether its buttons wrapped, which §6
       would rather have measured; the measured form is `PageShell`'s
       `--page-toolbar-h`, and the same treatment for this bar is recommended in
       the audit rather than built here — this file is over its line budget. */
    <div className={isDirty ? 'pb-32' : undefined}>
      {/* The eyebrow, not a date line drawn by the screen. Settings has no "now"
          of its own — nothing on it is dated — so the crumb carries only where
          you are. Rule 3.2: a screen never gets its own date, and the way to
          honour that on a screen with no date is to pass no date. */}
      <PageShell
        title={t('settings.title')}
        /* WHAT LIVES HERE AND WHAT DOES NOT. It cost a real hunt: an owner
           looked for two-factor under Security, found four password rules and
           concluded it could not be switched off. The line names the BOUNDARY,
           not the contents — those are searchable, one scroll down. */
        description={t(
          'settings.description',
          'Company-wide rules — everyone is subject to them. Anything that is yours alone, like your password or your authenticator, lives on your profile.'
        )}
        /* Search is how you navigate ~100 settings, so it is the screen's whole
            control area — and it is the SAME control area as everywhere else.
            `SearchField` replaces the hand-rolled icon-plus-`<Input>` this screen
            built: same slot, same bar, but the handoff's own anatomy — a recessed
            fill instead of a border, the magnifier as part of the control, and
            `min-w-search flex-search` instead of a definite width, so the field
            takes the slack on a monitor and gives it back before the bar wraps on a
            laptop. A bordered box was the one thing making the field a reader
            reaches for FIRST look like the eight fields they have to fill in.

            The match count stays in the bar's trailing status slot with its
            `role="status"`, so it is still announced when it changes rather than
            dangling under the input in its own `mt-2`. */
        toolbar={
          <ControlBar
            search={
              <SearchBox
                id="settings-search"
                ref={searchInputRef}
                label={t('settings.searchLabel')}
                placeholder={t('settings.searchPlaceholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            }
            status={
              needle !== '' && <p role="status">{t('settings.searchCount', { count: matchCount })}</p>
            }
          />
        }
      >
        {settings.isError ? (
          // A TOP-LEVEL card — it stands in for the whole settings body — so
          // `rounded-panel` (16px), not the 12px inner-card role. `border-danger-
          // border` is the hue's real border member rather than an alpha of its
          // solid fill, and `text-danger-on-surface` is the ink that belongs on the
          // pale tint (`text-danger` is the ink for the SOLID fill and measures
          // around 4.5:1 here, which is at the edge rather than clear of it).
          <div
            role="alert"
            className="rounded-panel border border-danger-border bg-danger-surface p-card shadow-resting"
          >
            <p className="text-copy font-medium text-danger-on-surface">
              {t('settings.loadError')}
            </p>
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="mt-3"
              onClick={() => void settings.refetch()}
            >
              {t('settings.retry')}
            </Button>
          </div>
        ) : data === undefined ? (
          // An unresolved query renders as a skeleton, never as a confident
          // value — a settings screen that shows a plausible default it has not
          // read is worse than one that shows nothing.
          <div className="space-y-component" aria-busy="true" aria-live="polite">
            <span className="sr-only">{t('settings.loading')}</span>
            {/* `rounded-panel`, matching the shape of the `SectionShell` cards
                these stand in for. A placeholder in a different corner radius from
                the thing it is holding a place for makes the page visibly re-shape
                when the data lands. */}
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-40 w-full rounded-panel" />
            ))}
          </div>
        ) : (
          <div className="space-y-section">
            {/* The group strip — horizontal, scrollable and STICKY; see
                `SettingsGroupNav` for why it sticks and why it is links rather
                than `Tabs`. Hidden while a search runs, because search
                deliberately crosses every group. */}
            {!searching && (
              <SettingsGroupNav
                groups={visibleGroups.map((g) => g.group)}
                activeGroupId={activeGroupId}
                dirtyByGroup={dirtyByGroup}
              />
            )}

            <div className="min-w-0 space-y-section">
              {/* Also a TOP-LEVEL card — it occupies the place a group of
                  `SectionShell`s would — so `rounded-panel`, and the hairline plus
                  the resting shadow that a card needs to read as one. */}
              {/* WHAT THE SEARCH DID NOT LOOK AT. Above the results rather
                  than below them: it changes how the list that follows should
                  be read, and a caveat under a short list is a caveat nobody
                  reaches. It names the way in — the groups keep their tabs, and
                  opening one asks for the PIN. */}
              {searching && heldBack.length > 0 && (
                <p className="rounded-panel border border-border bg-surface-2 p-card text-copy text-muted-foreground shadow-resting">
                  {/* Not a plural key. Every count sentence in this namespace
                      is one interpolated string — six Arabic plural forms
                      against two English ones is a parity the en/ar test
                      cannot express, and `settings.searchCount` settled the
                      convention. */}
                  {t('settings.searchLocked', { count: heldBack.length })}
                </p>
              )}

              {shownGroups.length === 0 && (
                <p className="rounded-panel border border-border bg-surface p-card text-copy text-muted-foreground shadow-resting">
                  {t('settings.noMatches', { query: query.trim() })}
                </p>
              )}

              {shownGroups.map(({ group, sections, showCustom }) => (
                <section
                  key={group.id}
                  id={`settings-group-${group.id}`}
                  aria-labelledby={`settings-group-${group.id}-title`}
                  className="space-y-component" style={{ scrollMarginTop: SETTINGS_GROUP_SCROLL_MARGIN }}
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-element">
                      {/* `text-h2` — the 19px SECTION-heading step, one above the
                          17px `text-h3` each `SectionShell` inside this group
                          carries. `text-lg font-semibold` was stock 18px/600 and
                          sat between the two steps of a scale it did not belong
                          to, so the group and its sections read as the same rank. */}
                      <h2
                        id={`settings-group-${group.id}-title`}
                        className="text-h2 text-foreground"
                      >
                        {t(`settings.g.${group.id}.title`)}
                      </h2>
                      {group.pay && <PayTag />}
                    </div>
                    <p className="mt-1 max-w-prose text-copy text-muted-foreground">
                      {t(`settings.g.${group.id}.blurb`)}
                    </p>
                  </div>

                  {/* Above the fields, not beside them: the admin has to learn
                      that these values are not in force BEFORE reading them as
                      policy. Gated on the sections actually on screen, so a
                      search that narrows the group to rules that ARE in force
                      (the OT multipliers, the weekend) doesn't carry a warning
                      about values it isn't showing. */}
                  {engineOff &&
                    sections.some(({ fields }) =>
                      fields.some((f) => f.category === 'defaultWorkday')
                    ) && <WorkdayEngineOffAlert reason={engineReason} />}

                  {/* Same placement rule, same reason: the admin has to learn
                      that "verify" is inert BEFORE reading the dropdown as a
                      policy choice. Gated on `face.fieldReviewMode` actually
                      being on screen, so a search that narrows this group to
                      other face settings doesn't carry a warning about a
                      control it isn't showing. */}
                  {faceReviewInoperative &&
                    sections.some(({ fields }) =>
                      fields.some((f) => f.id === 'face.fieldReviewMode')
                    ) && <FaceReviewInoperativeAlert reason={faceReviewReason} />}

                  {/* Tokens come BEFORE the templates that use them — reading
                      order matters more than component order here. */}
                  {showCustom && group.custom === 'letterTokens' && <LetterTokens />}

                  {/* Identity BEFORE wording, for the same reason: who you are
                      and how you look come before the nouns you use for
                      yourself. */}
                  {showCustom && group.custom === 'companyIdentity' && (
                    <CompanyIdentityBlock draft={company} />
                  )}

                  {sections.map(({ section, fields }) => (
                    <SectionShell
                      key={section.id}
                      id={`settings-section-${section.id}`}
                      title={t(`settings.s.${section.id}.title`)}
                      blurb={optional(`settings.s.${section.id}.blurb`)}
                      pay={section.pay}
                    >
                      <div className="divide-y divide-border">
                        {fields.map((field) => (
                          <FieldRow
                            key={field.id}
                            field={field}
                            value={currentValue(field)}
                            before={serverValue(field)}
                            disabled={
                              field.enabledBy
                                ? currentValue(
                                    FIELD_BY_ID.get(field.enabledBy) ?? field
                                  ) === false
                                : false
                            }
                            // Marked, never disabled. These values still matter:
                            // they are what takes effect the moment the engine is
                            // switched on, so the admin has to be able to set
                            // them BEFORE saving, not after.
                            notInForce={
                              (engineOff && field.category === 'defaultWorkday') ||
                              // Marked, never disabled — for the same reason.
                              // The admin must be able to LEAVE `verify`
                              // selected (it is the intent, and it starts
                              // working the day a client uploads a descriptor)
                              // or move off it, and both need the control live.
                              (faceReviewInoperative && field.id === 'face.fieldReviewMode')
                            }
                            onChange={setField}
                          />
                        ))}
                      </div>
                    </SectionShell>
                  ))}

                  {showCustom && group.custom === 'featurePresets' && (
                    <FeaturePresets
                      onApply={(preset) => {
                        setEdits((prev) => {
                          const next = { ...prev };
                          for (const [key, on] of Object.entries(preset)) {
                            next[`feature.${key}`] = on;
                          }
                          return next;
                        });
                        setFailed(null);
                      }}
                    />
                  )}

                  {/* Bare: `AppLockSettingsSection` is a `Card` whose own
                      `CardTitle` is already "Kiosk lock", so the wrapper was
                      printing that heading twice. Its button names its scope
                      instead of leaning on a sentence. */}
                  {showCustom && group.custom === 'kioskLock' && (
                    <AppLockSettingsSection />
                  )}

                  {showCustom && group.custom === 'managedElsewhere' && (
                    <ManagedElsewhere />
                  )}
                </section>
              ))}
            </div>
          </div>
        )}

        {failed && (
          <SettingsSaveFailure
            message={failed.message}
            onRestore={() => {
              setEdits((prev) => ({ ...prev, ...failed.values }));
              if (failed.company) company.restore(failed.company);
              setFailed(null);
            }}
            onClose={() => setFailed(null)}
          />
        )}

        {isDirty && (
          <SettingsSaveBar
            pendingCount={pendingCount}
            payCount={dirtyPayFields.length}
            obstacle={companyObstacle}
            busy={save.isPending}
            onDiscard={() => {
              setEdits({});
              company.clear();
              setFailed(null);
            }}
            onSave={() => void handleSave()}
          />
        )}

        <ConfirmPortal />
      </PageShell>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feature presets + the "managed elsewhere" index
// ---------------------------------------------------------------------------
